import { UAParser } from "ua-parser-js";

import {
  FiatCurrency,
  TransactionCurrency,
  Currency,
} from "@nestcoinco/hyper-api-gateway-api-client";

import { LEGAL_AGE, TRACKING_EVENTS } from "./constants";
import { showErrorToast, showSuccessToast } from "./show-toast";
import Logger from "./logger";
import { IpInfo } from "../types";
import { OperatingSystems } from "./vendor";

export function checkIsAndroidDevice() {
  const parser = new UAParser();
  const uaResult = parser.getResult();
  return !!uaResult.os.name?.includes("Android");
}

enum CurrencySymbols {
  NGN = "₦",
  USD = "$",
  HPR = "HPR",
  GEM = "GEM",
}

export function formatCurrency(
  value: number,
  currency?: TransactionCurrency | FiatCurrency | Currency,
  decimalPlace?: number
) {
  let symbol;
  switch (currency) {
    case TransactionCurrency.NGN:
    case FiatCurrency.NGN:
      symbol = CurrencySymbols.NGN;
      break;
    case TransactionCurrency.USD:
    case FiatCurrency.USD:
      symbol = CurrencySymbols.USD;
      break;
    case TransactionCurrency.HPR:
      symbol = CurrencySymbols.HPR;
      break;
    case TransactionCurrency.GEM:
      symbol = CurrencySymbols.GEM;
      break;
    default:
      symbol = "";
  }

  if (isNaN(value)) {
    return "Invalid number";
  }

  // Approximate value to the nearest whole number or decimal place provided and then to a string with commas
  let roundedValue = Math.round(value);
  if (!Number.isInteger(value) && decimalPlace) {
    roundedValue = +value.toFixed(decimalPlace);
  }
  const formattedNumber = roundedValue
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  if (!currency || currency in FiatCurrency) {
    return `${symbol}${formattedNumber}`;
  }
  return `${formattedNumber} ${symbol}`;
}

type TrackingEventNameType =
  (typeof TRACKING_EVENTS)[keyof typeof TRACKING_EVENTS];
export function registerTrackingEvent({
  name,
  payload,
}: {
  name: TrackingEventNameType;
  payload?: any;
}) {
  // Temporarily disabled all event tracking
  // (window as any)?.analytics?.track(name, payload);
}

export const getCurrentUrl = () => window.location.href;

export const getCurrentOrigin = () => window.location.origin;

type IdentityPayloadType = {
  name: string;
  username: string;
  email: string;
};

export function registerIdentityEvent(
  userId: string,
  payload: IdentityPayloadType
) {
  // Temporarily disabled all event tracking
  // (window as any)?.analytics?.identify(userId, {
  //   ...payload,
  //   sign_up_type: "web",
  // });
}

export function getTimeAgo(dateString?: string) {
  if (!dateString) return "";

  const inputDate = new Date(dateString);
  const now = new Date();

  const secondsAgo = Math.floor((now.getTime() - inputDate.getTime()) / 1000);
  const minutesAgo = Math.floor(secondsAgo / 60);
  const hoursAgo = Math.floor(minutesAgo / 60);

  if (secondsAgo < 60) {
    return `${secondsAgo} seconds ago`;
  } else if (minutesAgo < 60) {
    return `${minutesAgo} minutes ago`;
  } else {
    return `${hoursAgo} hours ago`;
  }
}

export async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    showSuccessToast("Copied successfully");
  } catch (error) {
    showErrorToast("Failed to copy");
  }
}

export function extractDecimalNumberParts(decimalNo: number): {
  wholeNumber: string;
  fraction?: string;
} {
  const [wholeNumber, fraction] = decimalNo.toString().split(".");
  return { wholeNumber, fraction };
}

function formatNumber(
  number: number,
  threshold = 5,
  determineDisplayedNumber: (divisionResult: number) => string
): string {
  const numberDigitCount = number.toString().length;
  if (numberDigitCount <= threshold) {
    return formatCurrency(number);
  } else if (numberDigitCount >= 7) {
    const divisor = 1000 * 1000; // 1 million
    return `${determineDisplayedNumber(number / divisor)}M`;
  } else {
    const divisor = 1000;
    return `${determineDisplayedNumber(number / divisor)}K`;
  }
}

export function formatHprBalance(balance: number, threshold?: number): string {
  const determineDisplayedBalance = (divisionResult: number) => {
    const { wholeNumber, fraction } = extractDecimalNumberParts(divisionResult);
    if (!fraction) return wholeNumber;

    const decimalFractionFirstDigit = +fraction[0];
    const decimalFractionSecondDigit = +fraction[1];
    if (!decimalFractionFirstDigit && !decimalFractionSecondDigit) {
      return divisionResult.toFixed(0);
    }
    if (decimalFractionFirstDigit && !decimalFractionSecondDigit) {
      return divisionResult.toFixed(1);
    }
    if (decimalFractionFirstDigit === 9 && decimalFractionSecondDigit === 9) {
      return wholeNumber + ".99";
    }
    return divisionResult.toFixed(2);
  };

  return formatNumber(balance, threshold, determineDisplayedBalance);
}

export function formatPlayCount(playCount: number, threshold = 4): string {
  const determineDisplayedPlayCount = (divisionResult: number) => {
    const { wholeNumber, fraction } = extractDecimalNumberParts(divisionResult);
    if (!fraction) return wholeNumber;

    const decimalFractionFirstDigit = +fraction[0];
    const decimalFractionSecondDigit = +fraction[1];
    if (isNaN(decimalFractionSecondDigit)) {
      return divisionResult.toFixed(1);
    }
    if (decimalFractionFirstDigit === 9) {
      return divisionResult.toFixed();
    }
    return `${wholeNumber}.${decimalFractionFirstDigit + 1}`;
  };

  return formatNumber(playCount, threshold, determineDisplayedPlayCount);
}

export enum TimeUnit {
  SECOND = "second",
  MINUTE = "minute",
  HOUR = "hour",
  DAY = "day",
}

export function getTimeDifferenceToNow(dateString?: string):
  | {
      difference: number;
      unit: TimeUnit;
    }
  | undefined {
  if (!dateString) return;

  const inputDate = new Date(dateString);

  const now = new Date();
  const nowUTC = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
      now.getUTCMinutes(),
      now.getUTCSeconds()
    )
  );

  const seconds = Math.floor((inputDate.getTime() - nowUTC.getTime()) / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.ceil(hours / 24);

  if (Math.abs(seconds) < 60) {
    return { difference: seconds, unit: TimeUnit.SECOND };
  } else if (Math.abs(minutes) < 60) {
    return { difference: minutes, unit: TimeUnit.MINUTE };
  } else if (Math.abs(hours) < 24) {
    return { difference: hours, unit: TimeUnit.HOUR };
  } else {
    return { difference: days, unit: TimeUnit.DAY };
  }
}

export function formatTimeAsCountdown(
  timeInMs: number,
  showDays?: boolean,
  showHours = true
) {
  if (timeInMs < 0) return undefined; // time is in past

  const formatNumber = (num: number) => (num < 10 ? `0${num}` : `${num}`);

  const diffInSeconds = Math.floor(timeInMs / 1000);
  const seconds = diffInSeconds % 60;
  const formattedSeconds = formatNumber(seconds);

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const minutes = diffInMinutes % 60;
  const formattedMinutes = formatNumber(minutes);

  const diffInHours = Math.floor(diffInMinutes / 60);
  const hours = diffInHours % 24;
  const formattedHours = formatNumber(hours);

  const days = Math.floor(diffInHours / 24);
  const formattedDays = formatNumber(days);

  if (showDays) {
    return `${formattedDays}d:${formattedHours}h:${formattedMinutes}m:${formattedSeconds}s`;
  }
  if (showHours) {
    const totalHours = days * 24 + hours;
    return `${formatNumber(
      totalHours
    )}:${formattedMinutes}:${formattedSeconds}`;
  }
  return `${formattedMinutes}:${formattedSeconds}`;
}

export function getTimeUntilFutureDate(futureDateStr: string): number {
  const futureDate = new Date(futureDateStr);
  const now = new Date();
  const diffInMs = futureDate.getTime() - now.getTime();

  return diffInMs;
}

export const getEndOfDay = (): string => {
  const now = new Date();

  return new Date(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1
  ).toISOString();
};

export const fetchIpInfo = async (): Promise<IpInfo | undefined> => {
  try {
    return (await (
      await fetch("https://ipinfo.io/json")
    ).json()) as Promise<IpInfo>;
  } catch (e) {
    Logger.error(e);
  }
};

export function checkIsScrolledToBottom(): boolean {
  const scrollTop = document.documentElement.scrollTop;
  const documentHeight = document.documentElement.scrollHeight;
  const windowHeight = window.innerHeight;

  return scrollTop + windowHeight >= documentHeight;
}

export function checkIsMobile(screenWidth?: number) {
  if (typeof window === "undefined") return false;

  const { innerWidth } = window;
  const MOBILE_SCREEN_THRESHOLD = 830;
  const parser = new UAParser();
  const uaResult = parser.getResult();

  return !!(
    (screenWidth || innerWidth) <= MOBILE_SCREEN_THRESHOLD ||
    uaResult.device.type === "mobile" ||
    uaResult.device.type === "tablet" ||
    navigator.userAgent.match(/iPhone/i) ||
    navigator.userAgent.match(/iPod/i) ||
    navigator.userAgent.match(/iPad/i) ||
    navigator.userAgent.match(/Android/i)
  );
}

export function checkBrowser() {
  if (typeof window === "undefined") return "";
  const parser = new UAParser();
  return parser.getBrowser().name;
}

export function checkOs() {
  if (typeof window === "undefined") return;

  const parser = new UAParser();
  const os = parser.getOS().name;
  if (os === "Android" && checkIsMobile()) {
    return OperatingSystems.Android;
  }
  if (os === "iOS") {
    return OperatingSystems.Ios;
  }
  if (os === "Mac OS") {
    return OperatingSystems.MacOs;
  }
}

export function createAppIntent(
  baseUrl: string,
  path: string,
  paramsObj?: Record<string, string>
) {
  const generateParamsString = (params: Record<string, string>): string => {
    let result: string = "";
    Object.entries(params).forEach((param, index, paramsArr) => {
      result += `${param[0]}=${param[1]}`;
      if (index === paramsArr.length - 1) return;
      result += "&";
    });
    return result;
  };

  const paramsStr = paramsObj ? generateParamsString(paramsObj) : "";
  return `intent://${baseUrl}${path}?${paramsStr}#Intent;scheme=https;action=android.intent.action.VIEW;end;`;
}

export enum AppType {
  PWA = "webapp",
  ANDROID = "play",
  IOS = "itunes",
}

export function openApp(customPath?: string, appType = AppType.PWA) {
  const { hostname: baseUrl, pathname: path } = window.location;
  const paramsObj = appType === AppType.PWA ? { source: "pwa" } : undefined;
  return window.open(
    createAppIntent(baseUrl, customPath || path, paramsObj),
    "_blank",
    "noopener"
  );
}

export function capitalizeFirstLetter(text: string) {
  if (!text) return "";
  const capitalFirstLetter = text[0].toUpperCase();
  const otherLetters = text.slice(1).toLowerCase();
  const result = capitalFirstLetter + otherLetters;
  return result;
}

export function calculateOriginalBundlePrice(
  price: number,
  amount: number,
  specialOffer?: number,
  bonusHpr?: number
) {
  if (specialOffer) {
    return (price / amount) * specialOffer;
  }
  if (bonusHpr && bonusHpr > 0) {
    return (price / amount) * (bonusHpr + amount);
  }
  return 0;
}

export const formatTransactionDate = (
  dateString: string,
  showYear?: boolean
) => {
  const formattedDate = new Date(dateString).toLocaleString("en-US", {
    year: showYear ? "numeric" : undefined,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
  return formattedDate;
};

export function convertToISODate(dateString: string) {
  const date = new Date(dateString);
  const formatter = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return formatter.format(date).replace(/\//g, "-");
}

export function calculateAge(initialDate: string, latterDate?: string) {
  const birth = new Date(initialDate);
  const endDate = latterDate ? new Date(latterDate) : new Date();
  let age = endDate.getFullYear() - birth.getFullYear();
  const monthDiff = endDate.getMonth() - birth.getMonth();

  // Adjust if birth date hasn't occurred yet this year
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && endDate.getDate() < birth.getDate())
  ) {
    age--;
  }

  return age;
}

export function isUpToLegalAge(dob = "") {
  return calculateAge(dob) >= LEGAL_AGE;
}

export async function checkGeoLocationPermission() {
  try {
    const permissionState = (
      await navigator.permissions.query({ name: "geolocation" })
    ).state;
    return permissionState;
  } catch (error) {
    Logger.log(error);
  }
}

export function getDeviceGeoLocation() {
  return new Promise<GeolocationPosition>((resolve, reject) => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
      });
    } else {
      reject(new Error("Geolocation is not supported by this browser."));
    }
  });
}

export function getCurrentDateParts(): {
  year: string;
  month: string;
  day: string;
  hours: string;
  minutes: string;
  seconds: string;
} {
  const currentDate = new Date();

  const year = currentDate.getFullYear().toString();
  const month = String(currentDate.getMonth() + 1).padStart(2, "0"); // Month is zero-indexed
  const day = String(currentDate.getDate()).padStart(2, "0");
  const hours = String(currentDate.getHours()).padStart(2, "0");
  const minutes = String(currentDate.getMinutes()).padStart(2, "0");
  const seconds = String(currentDate.getSeconds()).padStart(2, "0");

  return {
    year,
    month,
    day,
    hours,
    minutes,
    seconds,
  };
}

export function getOsSafeCurrentDateTime(): string {
  const { year, month, day, hours, minutes, seconds } = getCurrentDateParts();

  return `${year}_${month}_${day}_${hours}_${minutes}_${seconds}`;
}

export function getRandomNumberWithinRange(
  upperLimit: number,
  lowerLimit: number
) {
  return Math.floor(Math.random() * (upperLimit - lowerLimit + 1)) + lowerLimit;
}

export function replaceSpacesInString(str: string, replacementCharacter = "-") {
  return str.trim().toLowerCase().replace(/\s+/g, replacementCharacter);
}

function removeSpecialCharactersExceptSlashAndDash(input: string) {
  return input.replace(/[^a-zA-Z0-9/\-\s]/g, "");
}

export function formatUrl(url: string) {
  return removeSpecialCharactersExceptSlashAndDash(replaceSpacesInString(url));
}

export function addMinutesToDateTime(datetime?: string, minutes = 30): string {
  if (!datetime) return "";
  const date = new Date(datetime);
  date.setMinutes(date.getMinutes() + minutes);
  return date.toISOString();
}

export function removeTrailingSlash(text: string): string {
  return text.replace(/\/$/, "");
}

export function kebabToTitleCase(kebabString: string): string {
  return kebabString
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function getMetadataValue<
  T extends { metadata?: { name: string; value: string }[] },
>(entity: T, name: string): string | undefined {
  return entity.metadata?.find((m) => m.name === name)?.value;
}

export function getWeekStart(date: Date): Date {
  const d = new Date(date);
  d.setUTCDate(d.getUTCDate() - d.getUTCDay());
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

export function getWeekEnd(date: Date): Date {
  const start = getWeekStart(date);
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  end.setUTCHours(23, 59, 59, 999);
  return end;
}

export interface WeekRange {
  startDate: string;
  endDate: string;
}

export function getWeekRanges(): {
  thisWeek: WeekRange;
  lastWeek: WeekRange;
  twoWeeksAgo: WeekRange;
} {
  const now = new Date();

  const thisWeekStart = getWeekStart(now);
  const thisWeekEnd = getWeekEnd(now);

  const lastWeekEnd = new Date(thisWeekStart.getTime() - 1);
  const lastWeekStart = getWeekStart(lastWeekEnd);

  const twoWeeksAgoEnd = new Date(lastWeekStart.getTime() - 1);
  const twoWeeksAgoStart = getWeekStart(twoWeeksAgoEnd);

  return {
    thisWeek: {
      startDate: thisWeekStart.toISOString(),
      endDate: thisWeekEnd.toISOString(),
    },
    lastWeek: {
      startDate: lastWeekStart.toISOString(),
      endDate: lastWeekEnd.toISOString(),
    },
    twoWeeksAgo: {
      startDate: twoWeeksAgoStart.toISOString(),
      endDate: twoWeeksAgoEnd.toISOString(),
    },
  };
}

export function formatWeekRangeLabel(
  startDate: string,
  endDate: string
): string {
  const fmt = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
  return `${fmt.format(new Date(startDate))} - ${fmt.format(new Date(endDate))}`;
}
